import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { errorMessage } from "@/lib/api";
import { OmGlyph } from "@/components/VedicOrnaments";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// May 26 2026 — Email-first reordering.
// Google OAuth's fragment-based return is unreliable on Indian mobile networks
// (Chrome Android, DuckDuckGo, in-app webviews strip the URL hash before
// React mounts). Email login is rock-solid. Until Emergent ships query-param
// OAuth, we default users to the reliable path: email FIRST (primary button),
// Google SECOND (secondary, with a small caveat for mobile users).
const Login = () => {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const next = params.get("next") || "/app";

  // Jul 28 2026 · Surface the "session ended" toast when the global 401
  // interceptor (lib/api.js) redirects a signed-out user here. Firing
  // the toast from the interceptor itself is unreliable — the redirect
  // tears down the React tree before sonner can render — so we pass a
  // `reason` URL param and consume it on mount instead. Cleared from the
  // URL after showing so a browser refresh doesn't re-toast.
  useEffect(() => {
    const reason = params.get("reason");
    if (reason === "session_ended") {
      toast.error("Your session ended — please sign in again.");
      const clean = new URLSearchParams(params);
      clean.delete("reason");
      const qs = clean.toString();
      window.history.replaceState({}, "", qs ? `/login?${qs}` : "/login");
    }
  }, []);

  useEffect(() => { if (user) nav(next); }, [user, nav, next]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(next);
    } catch (err) {
      toast.error(errorMessage(err, "Sign in failed"));
    }
    setBusy(false);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Self-hosted Google OAuth (May 27 2026) — pure query-param return.
    // The backend builds the Google authorize URL using the request host
    // and 302-redirects. After Google bounces back, the backend exchanges
    // the code and 302s here to /auth/google?token=<jwt>.
    const params = new URLSearchParams();
    if (next && next !== "/app") params.set("next", next);
    const qs = params.toString() ? `?${params.toString()}` : "";
    window.location.href = `/api/auth/google/login${qs}`;
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <section className="py-16 md:py-24">
        <div className="max-w-md mx-auto px-6">
          <div className="text-center mb-10">
            <OmGlyph size={36} className="block mb-6" />
            <div className="section-label mb-4">Namaste</div>
            <h1 className="font-display text-5xl md:text-6xl text-ink leading-[0.88] mb-3">
              Welcome <span className="font-display-italic text-terracotta">back.</span>
            </h1>
            <p className="text-ink-soft">Return to your kundali</p>
          </div>

          {params.get("expired") && (
            <div className="bg-cream-soft border border-terracotta px-4 py-3 mb-6 flex items-start gap-3" data-testid="login-expired-banner">
              <span className="text-terracotta text-base leading-none mt-0.5" aria-hidden="true">⚠</span>
              <p className="text-sm text-ink leading-relaxed">
                We couldn&rsquo;t reach the server. Please try signing in again.
              </p>
            </div>
          )}

          {/* ── PRIMARY · Email login (reliable on all networks) ──────── */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-ink-muted mb-2 font-mono">Email</label>
              <input
                type="email"
                required
                className="input-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label className="block text-[10px] uppercase tracking-[0.3em] text-ink-muted font-mono">Password</label>
                <Link
                  to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                  className="text-[10px] uppercase tracking-[0.3em] text-ink-soft hover:text-terracotta font-mono transition-colors"
                  data-testid="login-forgot-password"
                >
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                required
                className="input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center"
              data-testid="login-submit"
            >
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>

          {/* ── divider ─────────────────────────────────────────────── */}
          <div className="divider-dots my-8 text-[10px] font-mono uppercase tracking-[0.3em]">or</div>

          {/* ── SECONDARY · Google (de-prioritised until query-param OAuth) ── */}
          <button
            onClick={googleLogin}
            className="w-full btn-ghost justify-center"
            data-testid="login-google"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" className="mr-2"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity=".7"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity=".5"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" opacity=".3"/></svg>
            Continue with Google
          </button>
          <p
            className="text-[11px] text-ink-muted mt-3 leading-relaxed font-mono uppercase tracking-[0.2em] text-center"
            data-testid="login-google-caveat"
          >
            <span className="normal-case tracking-normal text-ink-soft font-display-italic" style={{ fontFamily: "'Fraunces', serif" }}>
              On a slow mobile network? Email is the most reliable way in.
            </span>
          </p>

          <p className="text-center text-sm text-ink-soft mt-10">
            New to Bhrigu? <Link to="/signup" className="link-underline">Cast your first kundali</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Login;
